import React, { useCallback, useContext, useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useLocation, useParams } from "react-router-dom";
import config from "../../config";
import { AccessControlContext } from "../contexts/AccessControlContext";
import { AuthContext } from "../contexts/AuthContext";
import { useIsExpertOrGreater } from "../data/Results/useIsExpertOrGreater";
import { useUpdateReportStatus } from "../data/Results/useUpdateReportStatus";
import { useAddReportingMeasurements } from "../data/useAddReportingMeasurements";
import { useGetReportingMeasurementsByResultCapture } from "../data/useGetReportingMeasurementsByResultCapture";
import { useRemoveReportingMeasurements } from "../data/useRemoveReportingMeasurements";
import { useResultsList } from "../data/useResultsList";
import { ResultListRequestType } from "../shared/apiHandlers/ResultsAPIs";
import { Card } from "../shared/Card";
import {
  MeasurementComponent,
  MeasurementType,
  ReportStatuses,
  ResultDataPointTypes,
  SubLocation,
} from "../shared/enums/Result";
import { doesAssetHaveMedia, getMeasurementsByMeasurementType } from "../shared/helpers/ResultHelpers";
import { getSubstationDisplayName } from "../shared/helpers/ResultUtils";
import { IdName } from "../shared/interfaces/Asset";
import { ReportingMeasurement } from "../shared/interfaces/ReportingMeasurement";
import {
  Measurement,
  MeasurementDetail,
  PanelMeasurement,
  ReportStatusUpdate,
  Result,
  ResultSurveyableAsset,
} from "../shared/interfaces/Result";
import { MessageToast, ToastMode } from "../shared/MessageToast";
import RouteBreadcrumbs from "../shared/RouteBreadcrumbs";
import { Snackbar } from "../shared/Snackbar";
import { ExportResultCSVButton } from "./ExportResultCSVButton";
import { NumericalTabsList } from "./MeasurementDetails";
import { MeasurementDialog, useMeasurementPageURLFactory } from "./MeasurementViewer";
import { MediaViewer } from "./MediaViewer/MediaViewer";
import { PanelMeasurementsOverview } from "./PanelMeasurementsOverview";
import { usePartialResultReducer } from "./PartialResultReducer";
import { PartialResultStepper } from "./PartialResultStepper";
import { ResultMetadata } from "./ResultMetadata";
import { SurveyableAssetsOverview } from "./SurveyableAssetsOverview";
import { storeMeasurementInSession } from "./utils/storeMeasurementInSession";
import { managedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";

interface LocationState {
  result: Result;
}

export function ResultViewer() {
  const { t } = useTranslation();

  //if rendering via the ResultGrid, the props should be passed via react router's location.state
  const location = useLocation();
  const locationState = location.state as LocationState;

  //get the captureID and assetID from the URL
  const { resultType, captureId, switchgearAssetId, customerId, companyId, siteId } = useParams<{
    resultType: "bySwitchgear" | "byCaptureId" | "byHVSubstation";
    captureId?: string;
    switchgearAssetId?: string;
    customerId: string;
    companyId: string;
    siteId: string;
  }>();

  const [requestType, setRequestType] = useState<ResultListRequestType>();
  const resultsList = useResultsList(requestType, {
    onSuccess: onFetchResultsSuccess,
  });

  const [selectedResult, setSelectedResult] = useState<Result | undefined>(locationState?.result);
  const [pageLoading, setPageLoading] = useState(resultsList.isLoading);
  const [measurementViewerOpen, setMeasurementViewerOpen] = useState(false);
  const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementDetail<Measurement> | undefined>();
  const [position, setPosition] = useState<number | undefined>();
  const [measurementHistory, setMeasurementHistory] = useState<MeasurementDetail<Measurement>[] | undefined>();
  const [measurementHistoryWithLatest, setMeasurementHistoryWithLatest] = useState<
    MeasurementDetail<Measurement>[] | undefined
  >();
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [selectedMediaAsset, setSelectedMediaAsset] = useState<IdName | undefined>();
  const [selectedAsset, setSelectedAsset] = useState<ResultSurveyableAsset | undefined>(
    selectedResult?.dataValue.Assets?.[0],
  );
  const [isPartialResult, setIsPartialResult] = useState<boolean>(
    selectedResult?.dataPointTypeId === ResultDataPointTypes.Partial,
  );
  const partialResultReducer = usePartialResultReducer(selectedResult);

  const substationDisplayName = selectedResult && getSubstationDisplayName(selectedResult);

  const isExpertOrGreater = useIsExpertOrGreater(
    Number(customerId),
    selectedResult?.dataValue.SubstationSummary?.SubstationId,
    substationDisplayName,
  );
  const isEaEmployee = useContext(AuthContext).amIEaEmployee();
  const accessControlContext = useContext(AccessControlContext);
  const canAddResult = accessControlContext.canI("add", "results", Number(customerId));

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const addReportingMeasurements = useAddReportingMeasurements({
    onSuccess: handleAddReportingMeasurementsSuccess,
    onError: handleAddReportingMeasurementsError,
  });
  const removeReportingMeasurements = useRemoveReportingMeasurements({
    onSuccess: handleRemoveReportingMeasurementsSuccess,
    onError: handleRemoveReportingMeasurementsError,
  });

  const reportingMeasurementsByResultCapture = useGetReportingMeasurementsByResultCapture(
    Number(captureId),
    Number(customerId),
    Number(switchgearAssetId),
  );
  const queryClient = useQueryClient();

  const updateReportStatus = useUpdateReportStatus({
    onSuccess: handleUpdateReportStatusSuccess,
    onError: handleUpdateReportStatusError,
  });

  useEffect(() => {
    if (captureId && resultType === "byCaptureId")
      setRequestType({
        type: "byCaptureId",
        captureId: captureId,
        customerId: Number(customerId),
      });
    else if (switchgearAssetId && resultType === "byHVSubstation")
      setRequestType({
        type: "byHVSubstation",
        substationId: Number(switchgearAssetId),
      });
    else if (switchgearAssetId && !isPartialResult)
      setRequestType({ type: "bySwitchgear", switchgearId: switchgearAssetId });
  }, [resultType, switchgearAssetId, captureId, customerId, isPartialResult, selectedResult?.dataValue.Assets]);

  useEffect(() => {
    setPageLoading(resultsList.isLoading);
  }, [resultsList.isLoading]);

  useEffect(() => {
    setSelectedAsset(selectedResult?.dataValue.Assets?.[0]);
  }, [selectedResult]);

  function onFetchResultsSuccess(results: Result[]) {
    if (!selectedResult && results) {
      const foundResult = results.find((result) => result.captureId.toString() === captureId);
      const foundResultIsPartialResult = foundResult?.dataPointTypeId === ResultDataPointTypes.Partial;

      setIsPartialResult(foundResultIsPartialResult);

      //if we've fetched "byCaptureId", we weren't sure if it was going to be a partial or complete result. Check the result, and if it's "complete", fetch the history
      if (
        foundResult &&
        switchgearAssetId &&
        foundResult.dataPointTypeId === ResultDataPointTypes.Complete &&
        requestType?.type === "byCaptureId"
      ) {
        setRequestType({
          type: "bySwitchgear",
          switchgearId: switchgearAssetId,
        });
      }

      foundResult && setSelectedResult(foundResult);
      foundResult && foundResultIsPartialResult && partialResultReducer?.setResult(foundResult);

      if (foundResult?.status === ReportStatuses.NotViewed) {
        const reportStatusUpdate: ReportStatusUpdate = {
          captureId: Number(captureId),
          status: ReportStatuses.Viewed,
        };

        config.reportsEnabled && isEaEmployee && updateReportStatus.mutate(reportStatusUpdate);
      }
    }
  }

  function handleUpdateReportStatusSuccess() {
    setSnackbarText(t("results.updateReportStatusViewedSuccess"));
    setShowSnackbar(true);
  }

  function handleUpdateReportStatusError() {
    setSnackbarText(t("results.updateReportStatusViewedError"));
    setShowSnackbar(true);
  }

  const handleMeasurementViewerClose = useCallback(() => {
    setSelectedMeasurement(undefined);
    setMeasurementViewerOpen(false);
  }, []);

  const createMeasurementPageURL = useMeasurementPageURLFactory();

  function handleIconClick(
    measurementDetail: MeasurementDetail<Measurement>,
    position: number | undefined,
    newTab: boolean,
  ) {
    if (newTab) {
      storeMeasurementInSession({
        measurementDetail,
        measurementHistory,
        selectedResult,
        isPartialResult,
        instrument: selectedResult?.dataValue.MetaData.Instrument,
        position,
        isExpertOrGreater: isExpertOrGreater.data?.valueOf(),
        isEaEmployee: isEaEmployee,
      });

      const measurementURL = createMeasurementPageURL(
        requestType?.type,
        captureId,
        switchgearAssetId,
        measurementDetail?.measurement,
        customerId,
        companyId,
        siteId,
      );
      const win = window.open(measurementURL, "_blank");
      win?.focus();
    } else {
      setSelectedMeasurement(measurementDetail);
      setPosition(position);
      setMeasurementViewerOpen(true);
    }
  }

  useEffect(() => {
    if (selectedMeasurement) {
      const historicalMeasurementsWithLatest: MeasurementDetail<Measurement>[] = resultsList.data?.reduce(
        (measurements: any, result: Result) => {
          let panelMeasurement: PanelMeasurement | undefined;
          if (result.dataValue.PanelMeasurements?.length > 0) {
            panelMeasurement = result.dataValue.PanelMeasurements.find(
              (panelMeasurement) => panelMeasurement.Panel360AssetId === selectedMeasurement?.panel360AssetId,
            );
          } else {
            panelMeasurement = result.dataValue.Assets.flatMap((asset) =>
              asset.PanelMeasurements.filter(
                (panelMeasurement) => panelMeasurement.Panel360AssetId === selectedMeasurement?.panel360AssetId,
              ),
            )[0];
          }

          const measurementsByType =
            panelMeasurement && getMeasurementsByMeasurementType(panelMeasurement, selectedMeasurement.measurementType);
          const historicalMeasurementDetail = measurementsByType
            ?.filter(
              (measurement) =>
                measurement.ComponentAssetId === selectedMeasurement.measurement.ComponentAssetId &&
                (measurement.SubLocation ?? SubLocation.None) ===
                  (selectedMeasurement.measurement.SubLocation ?? SubLocation.None),
            )
            .map((measurement) => {
              return {
                measurement: measurement,
                measurementType: measurement.MeasurementType,
                panelName: selectedMeasurement.panelName,
                panelNo: selectedMeasurement.panelNo,
                panel360AssetId: selectedMeasurement.panel360AssetId,
                time: result.time,
                captureId: result.captureId,
                instrument: result.dataValue.MetaData.Instrument,
              };
            });

          if (historicalMeasurementDetail && historicalMeasurementDetail.length > 0)
            return [...measurements, ...historicalMeasurementDetail];
          else return measurements;
        },
        [],
      );

      const historicalMeasurements = historicalMeasurementsWithLatest?.filter(
        (measurementDetail) =>
          selectedResult &&
          selectedResult.time &&
          measurementDetail.time &&
          new Date(measurementDetail.time) < new Date(selectedResult.time),
      );

      setMeasurementHistoryWithLatest(historicalMeasurementsWithLatest);
      setMeasurementHistory(historicalMeasurements);
    }
  }, [selectedMeasurement, resultsList.data, selectedResult]);

  const handleMediaViewerClose = useCallback(() => {
    setMediaViewerOpen(false);
  }, []);

  function openMediaViewer(mediaAsset?: IdName) {
    setSelectedMediaAsset(mediaAsset);
    setMediaViewerOpen(true);
  }

  async function handleMeasurementChange() {
    setPageLoading(true);

    setSnackbarText(t("common.dataUpdated", { entity: t("common.status") }));
    setShowSnackbar(true);

    const refreshedResults = await resultsList.refetch();

    // Refresh the selected result
    if (selectedResult) {
      const updatedResult = refreshedResults.data?.find((result) => result.captureId === selectedResult.captureId);
      setSelectedResult(updatedResult);
      setSelectedMeasurement(undefined);
    }

    setPageLoading(false);
  }

  async function handleImageUploadSuccess() {
    setSnackbarText(t("results.imageUploadSuccess"));
    setShowSnackbar(true);

    const refreshedResults = await resultsList.refetch();

    if (selectedResult) {
      const updatedResult = refreshedResults.data?.find((result) => result.captureId === selectedResult.captureId);
      setSelectedResult(updatedResult);
    }
  }

  async function handleImageUploadError(response: Response) {
    let responseText = await response.text();
    if (responseText === "") responseText = t("results.imageUploadError");
    setSnackbarText(responseText);
    setShowSnackbar(true);
  }

  function handleCheckboxChange(
    isChecked: boolean,
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
    chartIndex?: number | null,
  ) {
    if (isChecked) {
      const measurementData = {
        customerId: Number(customerId),
        assetId: Number(switchgearAssetId),
        chartCaptureId: captureId,
        resultCaptureId: resultCaptureId,
        chartDataPointId: dataPointId,
        chartTime: time,
        tabPosition,
        componentType,
        measurementType,
        subLocation: subLocation ?? SubLocation.None,
        panel360AssetId,
        panelNo,
        panelName,
        chartIndex,
      };
      addReportingMeasurements.mutate(measurementData);
    } else {
      const measurementData = {
        customerId: Number(customerId),
        assetId: Number(switchgearAssetId),
        chartCaptureId: captureId,
        resultCaptureId: resultCaptureId,
        chartDataPointId: dataPointId,
        chartTime: time,
        tabPosition,
        componentType,
        measurementType,
        subLocation: subLocation ?? SubLocation.None,
        panel360AssetId,
        panelNo,
        panelName,
        chartIndex,
      };
      if (measurementData.chartIndex === undefined) {
        measurementData.chartIndex = null;
      }
      const measurementDataToDelete = reportingMeasurementsByResultCapture.data?.find(
        (measurement: ReportingMeasurement) =>
          measurement.chartDataPointId === measurementData.chartDataPointId &&
          measurement.chartCaptureId === measurementData.chartCaptureId &&
          measurement.resultCaptureId === measurementData.resultCaptureId &&
          measurement.chartTime === measurementData.chartTime &&
          measurement.tabPosition === measurementData.tabPosition &&
          measurement.componentType === measurementData.componentType &&
          measurement.measurementType === measurementData.measurementType &&
          (measurement.subLocation ?? SubLocation.None) === (measurementData.subLocation ?? SubLocation.None) &&
          measurement.panel360AssetId === measurementData.panel360AssetId &&
          measurement.panelNo === measurementData.panelNo &&
          measurement.panelName === measurementData.panelName &&
          measurement.chartIndex === measurementData.chartIndex,
      );
      if (measurementDataToDelete) {
        removeReportingMeasurements.mutate(measurementDataToDelete);
      }
    }
  }

  function handleClearSelectedCharts(
    captureId: number,
    resultCaptureId: number,
    dataPointId: number,
    time: string,
    tabPosition: NumericalTabsList,
    componentType: MeasurementComponent,
    measurementType: MeasurementType,
    subLocation: SubLocation,
    panel360AssetId: number,
    panelNo: string,
    panelName: string,
  ) {
    const measurementsToDelete = reportingMeasurementsByResultCapture.data?.filter(
      (measurement: ReportingMeasurement) =>
        measurement.chartCaptureId === captureId &&
        measurement.resultCaptureId === resultCaptureId &&
        measurement.chartDataPointId === dataPointId &&
        measurement.chartTime === time &&
        measurement.tabPosition === tabPosition &&
        measurement.componentType === componentType &&
        measurement.measurementType === measurementType &&
        (measurement.subLocation ?? SubLocation.None) === (subLocation ?? SubLocation.None) &&
        measurement.panel360AssetId === panel360AssetId &&
        measurement.panelNo === panelNo &&
        measurement.panelName === panelName,
    );

    if (measurementsToDelete)
      measurementsToDelete.forEach((measurement: ReportingMeasurement) => {
        removeReportingMeasurements.mutate(measurement);
      });
  }

  function handleAddReportingMeasurementsSuccess() {
    queryClient.invalidateQueries({ queryKey: ["reportingMeasurements"] });
  }

  function handleRemoveReportingMeasurementsSuccess() {
    queryClient.invalidateQueries({ queryKey: ["reportingMeasurements"] });
  }

  function handleAddReportingMeasurementsError() {
    setSnackbarText(t("results.addReportingMeasurementError"));
    setShowSnackbar(true);
  }

  function handleRemoveReportingMeasurementsError() {
    setSnackbarText(t("results.removeReportingMeasurementError"));
    setShowSnackbar(true);
  }

  function handleAssetChange(asset: ResultSurveyableAsset) {
    setSelectedAsset(asset);
  }

  if (!selectedResult && resultsList.isError) {
    return (
      <div className="w-100 d-flex justify-content-center pt-2">
        {resultsList.error?.status === 403 ? (
          <MessageToast
            text={t("common.operationForbiddenError", {
              operation: t("common.view"),
              item: "$t(results.result)",
            })}
            mode={ToastMode.Warning}
            show
          />
        ) : (
          <MessageToast
            text={
              resultsList.errorMessage != null
                ? resultsList.errorMessage.replace(/['"]/g, "")
                : t("common.dataFetchError", { val: "$t(results.result)" })
            }
            mode={ToastMode.Error}
            show
          />
        )}
      </div>
    );
  } else {
    return (
      <React.Fragment>
        {selectedResult && (
          <MediaViewer
            mediaMetaData={selectedResult.dataValue.MediaMetaData}
            open={mediaViewerOpen}
            onClose={handleMediaViewerClose}
            selectedAsset={selectedMediaAsset}
            isPartialResult={isPartialResult}
          />
        )}
        {selectedMeasurement && selectedResult && (
          <MeasurementDialog
            measurementDetail={selectedMeasurement}
            selectedResult={selectedResult}
            measurementHistory={measurementHistory}
            measurementHistoryWithLatest={measurementHistoryWithLatest}
            open={measurementViewerOpen}
            onClose={handleMeasurementViewerClose}
            onChange={handleMeasurementChange}
            instrument={selectedResult.dataValue.MetaData.Instrument}
            position={position}
            resultType={requestType?.type}
            customerId={customerId}
            companyId={companyId}
            siteId={siteId}
            isPartialResult={isPartialResult}
            isLoading={pageLoading}
            onCheckboxChange={handleCheckboxChange}
            isExpertOrGreater={isExpertOrGreater.data?.valueOf()}
            isEaEmployee={isEaEmployee}
            reportingMeasurements={reportingMeasurementsByResultCapture.data}
            onClearSelectedCharts={handleClearSelectedCharts}
            selectedAssetType={selectedAsset?.AssetType || managedSurveyAssetType.Switchgear}
          />
        )}
        <Container fluid>
          <Row>
            <Col className="pb-3">
              <RouteBreadcrumbs
                customPageTitle={
                  pageLoading
                    ? t("results.result")
                    : substationDisplayName !== undefined && substationDisplayName !== ""
                      ? `${t("results.result")} - ${substationDisplayName} - ${t("common.formattedDate", { dateString: selectedResult?.time })}`
                      : `${t("results.result")} - ${t("common.formattedDate", { dateString: selectedResult?.time })}`
                }
              />
            </Col>
            {selectedResult && !pageLoading && selectedResult.dataValue.Assets == null && (
              <Col xs="auto">
                <ExportResultCSVButton result={selectedResult} />
              </Col>
            )}
          </Row>
          <Row>
            <Col lg={4}>
              {selectedResult && isPartialResult && (
                <div className="pb-3">
                  <PartialResultStepper partialResult={selectedResult} partialResultReducer={partialResultReducer} />
                </div>
              )}
              <ResultMetadata
                result={selectedResult}
                isPartialResult={isPartialResult}
                pageLoading={pageLoading}
                enableMediaIcon={selectedResult ? doesAssetHaveMedia(selectedResult, selectedResult.assetId) : false}
                onMediaClick={openMediaViewer}
                customerId={Number(customerId)}
                canAddResult={canAddResult?.valueOf()}
                onImageUploadSuccess={handleImageUploadSuccess}
                onImageUploadError={handleImageUploadError}
                selectedAsset={selectedAsset}
              />
            </Col>
            <Col lg={8}>
              <Card title={t("results.measurements")} loading={pageLoading} sx={{ marginBottom: 2 }}>
                {selectedResult?.dataValue.Assets ? (
                  <SurveyableAssetsOverview
                    result={selectedResult}
                    onIconClick={handleIconClick}
                    onMediaClick={openMediaViewer}
                    isPartialResult={isPartialResult}
                    partialResultReducer={partialResultReducer}
                    onImageUploadSuccess={handleImageUploadSuccess}
                    onImageUploadError={handleImageUploadError}
                    canAddResult={canAddResult?.valueOf()}
                    onAssetChange={handleAssetChange}
                  />
                ) : (
                  <PanelMeasurementsOverview
                    result={selectedResult}
                    onIconClick={handleIconClick}
                    onMediaClick={openMediaViewer}
                    isPartialResult={isPartialResult}
                    partialResultReducer={partialResultReducer}
                    onImageUploadSuccess={handleImageUploadSuccess}
                    onImageUploadError={handleImageUploadError}
                    panelMeasurements={selectedResult?.dataValue.PanelMeasurements}
                    canAddResult={canAddResult?.valueOf()}
                  />
                )}
              </Card>
            </Col>
          </Row>
        </Container>
        <Snackbar
          open={showSnackbar}
          onClose={() => {
            setShowSnackbar(false);
          }}
          message={snackbarText}
        />
      </React.Fragment>
    );
  }
}
